import { max } from "@foretagsplatsen/core-helpers/array";
import AgedAccountsSummaryGroup from "./AgedAccountsSummaryGroup.js";
import Model from "../../Model.js";

/**
 * `AgedAccountsSummary` represents a summary of all aged accounts values (receivables or
 * payables) making no distinction between the clients, but summarizing the total amount
 * per period, as well as the total amount for this company.
 * It is used by the aged account summary chart and table widgets.
 *
 * As an example, the following table shows a summary with 2 AgedAccountsSummaryGroup
 * (one per row):
 *
 * | label      |   value | legend  | percentage |
 * |------------+---------+---------+------------|
 * | On time    |    1984 | on-time |        42% |
 * | 31-60 days | 4723.80 | 31-60   |        58% |
 * |------------+---------+---------+------------|
 * | Total      | 6707.80 |         |            |
 *
 * In this example, the summary total is 6707.80.
 *
 * @param {number} total - sum of all aged accounts summary groups values.
 * @param {AgedAccountsSummaryGroup[]} groups - summary of aged accounts values
 * sorted by periods (e.g., 31 to 60 days old).
 */
export default class AgedAccountsSummary extends Model {
	constructor({ total = 0, groups = [] }) {
		super(...arguments);

		this._total = total;
		this._groups = groups;

		if (groups.length) {
			this._adjustPercentagesSum();
		}
	}

	getTotal() {
		return this._total;
	}

	getGroups() {
		return this._groups.slice();
	}

	isEmpty() {
		return (
			this._total === 0 &&
			this._groups.every((group) => group.getValue() === 0)
		);
	}

	hasNegativeValue() {
		return this._groups.some((group) => group.isNegative());
	}

	/**
	 * This method adjusts the bigger percentage to fix the cases when the total percentage
	 * of all groups does not reach 100% (due to the rounding of percentages).
	 */
	_adjustPercentagesSum() {
		const allPercentages = this._groups.map((group) =>
			group.getPercentage(),
		);
		const highestPercentageGroup = max(this._groups, (group) =>
			group.getPercentage(),
		);

		const allPercentageSum = allPercentages.reduce(
			(sum, percentage) => sum + percentage,
			0,
		);

		const adjustedPercentage =
			100 - allPercentageSum + highestPercentageGroup.getPercentage();

		highestPercentageGroup.setPercentage(adjustedPercentage);
	}

	static transformMaterializationData(data, context) {
		(data.groups || []).forEach((group) => {
			group.summaryTotal = data.total;
		});

		data.groups = AgedAccountsSummaryGroup.materializeAll(
			data.groups,
			context,
		);
	}
}
